import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { apiBaseUrl, apiVersion } from '../config';
import {
  DEFAULT_UNIT_SETTINGS,
  ToastV2Service,
  UnitSettingsV2Service,
  retryAfterTimeout,
} from '@schenck/rotec-components';
import { Units } from '../utils/interfaces/settings.interface';
import { Observable } from 'rxjs';
import { ExportUserSettings } from 'src/utils/interfaces/export-settings.interface';
import { unitDefaults } from 'src/utils/constants/settings.const';
import { TranslateService } from '@ngx-translate/core';
import { Languages } from 'src/utils/enums/languages.enum';
import { ErrorHandlerService } from 'src/services/error-handler/error-handler.service';

export interface AverageRotor {
  /**
   * A hardcoded special id for the average rotor from the backend
   */
  id: string;
  /**
   * The measurements
   */
  measurements: RotorMeasurement[];
}

export interface RotorForComparison {
  /**
   * The unique id.
   */
  id: string;
  /**
   * The rotor id.
   */
  rotorId: string;
  /**
   * Type of the rotor.
   */
  typeName: string;
  /**
   * Timestamp of production date.
   */
  timestamp?: Date;
  /**
   * List of measurements.
   */
  measurements?: RotorMeasurement[];
}

export interface RotorMeasurement {
  measurementId?: string;
  header?: {
    user?: string;
    date?: Date;
    machineId?: string;
    machineName?: string;
    configurationId?: string;
    programId?: string;
    operator?: string;
    customer?: string;
    projectNo?: string;
    orderNo?: string;
  };
  typeData?: {
    rotorType?: string;
    rotorClass?: string;
    distanceA?: number;
    distanceB?: number;
    distanceC?: number;
    radius1?: number;
    radius2?: number;
    calculationMethod?: string;
    deviation?: number;
    balancingQualityGrade?: string;
    massOfRotor?: number;
    dynamicTolerancePerPlane?: number;
    momentTolerance?: number;
    staticTolerance?: number;
    balancingSpeed?: number;
    serviceSpeed?: number;
    speedWindow?: Date; // ??
  };
  result?: {
    totalNoOfRuns?: number;
    runs?: {
      no?: number;
      speed?: number;
      unbal?: {
        static?: Measurement;
        dynamic?: Measurement[];
        moment?: Measurement[];
      };
      correction?: {
        no?: number;
        actions?: {
          method?: string;
          mass?: number;
          angle?: number;
          angleStart?: number;
          angleEnd?: number;
          weightAmount?: number;
          weightAngle?: number;
          millingDepth?: number;
        }[];
      }[];
    }[];
  };
}

export interface AverageTile {
  id: string;
  rotorId: string;
  typeName: string;
  measurements: RotorMeasurement[];
}

export interface Measurement {
  absolute: number;
  unit: string;
  angle: number;
  tolerance: number;
}

@Injectable()
export class RotorApi {
  private readonly http = inject(HttpClient);
  private readonly unitSettings = inject(UnitSettingsV2Service);
  private readonly translateService = inject(TranslateService);
  private readonly errorHandlerService = inject(ErrorHandlerService);
  private readonly toastService = inject(ToastV2Service);

  get(rotorId: string, decimals: number, units: Units): Observable<RotorForComparison> {
    return this.http
      .get(
        `${apiBaseUrl}/rotor/summary/${rotorId}?decimals=${decimals}&unbalUnit=${units.unbalUnit}&lengthUnit=${units.lengthUnit}&weightUnit=${units.weightUnit}&rotationUnit=${units.rotationUnit}&isUnbalanceAsMass=${units.isUnbalanceAsMass}&api-version=${apiVersion}`
      )
      .pipe(
        retryAfterTimeout(2000, 5),
        catchError((error) => {
          this.toastService.openError(error?.error?.message);
          this.errorHandlerService.setSearchError(error?.status ?? error?.error ?? true);
          throw error;
        }),
        map((result: any) => ({
          ...result,
        }))
      );
  }

  average(ids: string[], decimals: number, units: Units = unitDefaults): Observable<AverageRotor> {
    return this.http.post<AverageRotor>(
      `${apiBaseUrl}/rotor/summary/average?api-version=${apiVersion}`,
      {
        ids: ids,
        decimals,
        unbalUnit: units.unbalUnit,
        lenghtUnit: units.lengthUnit,
        weightUnit: units.weightUnit,
        rotationUnit: units.rotationUnit,
        isUnbalanceAsMass: units.isUnbalanceAsMass,
      }
    );
  }

  exportCSV(ids: string[]) {
    return this.http.post(
      `${apiBaseUrl}/rotor/export/csv?api-version=${apiVersion}`,
      {
        ids: ids,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: Languages[this.translateService.currentLang],
        decimals:
          typeof this.unitSettings.state.units?.decimals === 'number'
            ? this.unitSettings.state.units.decimals
            : DEFAULT_UNIT_SETTINGS.decimals,
      },
      {
        responseType: 'blob',
      }
    );
  }

  exportPDF(data: ExportUserSettings) {
    const { ids, logo, language, decimals, units, signatureField, measuringRuns } = data;
    return this.http.post(
      `${apiBaseUrl}/rotor/export/pdf?api-version=${apiVersion}`,
      {
        ids,
        decimals,
        logo,
        language,
        unbalUnit: units.unbalUnit,
        lenghtUnit: units.lengthUnit,
        weightUnit: units.weightUnit,
        rotationUnit: units.rotationUnit,
        isUnbalanceAsMass: units.isUnbalanceAsMass,
        //commentField,  // Comment box in the cloud and on the machine does not affect in the report. It will be hide the checkbox unless we have implemented a workable solution.
        signatureField,
        measuringRuns,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      {
        responseType: 'blob',
      }
    );
  }
}
